import { juxt } from 'ramda'
import Raven from 'raven-js'

import ability from '@/helpers/ability'
import auth from '@/mixins/auth'
import {
  getUser,
  isToNewLayout,
  isEnterprisePlan,
  isAdminOrManager,
  isEarlyAccessTrialAccount,
  generateCookie,
} from '@/service/user'

const setSentryContextUser = () => Raven.setUserContext(getUser())

const logoutValidation = ({ to }) => {
  if (to.path.endsWith('/logout')) {
    auth.logout()

    const redirectUrl = new URLSearchParams(
      document.location.search.substring(1)
    ).get('redirectUrl')
    if (redirectUrl) {
      sessionStorage.setItem('redirectUrl', redirectUrl)
    }

    window.location = '/login'
    throw new Error('redirect to "/login"')
  }
}

const redirectValidation = ({ to, next }) => {
  if (!auth.isAuthenticated() && !to.path.split('/').includes('login')) {
    localStorage.redirectAfterLogin = to.path
    next({ path: '/login' })
  }
}

const routerPermissionValidation = ({ to, next }) => {
  const { permission = 'page.user-access' } = to.meta

  if (!ability().can('access', permission)) {
    next({ path: '/dashboard' })
    return null
  }
}

const redirectToNew = async to => {
  const env = window.env || process.env
  const host = env.VUE_APP_NEW_LINK

  let toURL =
    typeof to.meta.redirect.to === 'function'
      ? to.meta.redirect.to(isAdminOrManager())
      : to.meta.redirect.to

  if (toURL.includes(':')) {
    const urlPath = toURL.split('/')
    const urlPatternParamsRequired = toURL
      .match(/:(\w+)/g)
      .map(param => param.slice(1))

    urlPatternParamsRequired.forEach(param => {
      if (to.params[param]) {
        const paramValue = to.params[param]
        const paramIndex = urlPath.indexOf(`:${param}`)

        if (paramIndex > -1) {
          urlPath[paramIndex] = paramValue
        }
      }
    })

    toURL = urlPath.join('/')
  }

  const redirectTo = new URL(toURL, host)
  redirectTo.searchParams.append('l', localStorage.languageabrev)

  await generateCookie()

  window.location = redirectTo.toString()
}

const doNext = async ({ to, next }) => {
  const canAccessNewLayout =
    (isEnterprisePlan() || isEarlyAccessTrialAccount()) &&
    !ability().can('access', 'read-only')

  if (canAccessNewLayout) {
    const env = window.env || process.env

    const isRedirectToNewURL = to.query && to.query['redirect-new']
    if (isRedirectToNewURL) {
      await generateCookie()

      const host = env.VUE_APP_NEW_LINK
      const redirectTo = new URL(to.query['redirect-new'], host)
      redirectTo.searchParams.append('l', localStorage.languageabrev)

      window.location = redirectTo.toString()
      return
    }

    const isRedirectToNewLayout =
      to.query &&
      to.query.redirect === 'new-layout' &&
      to.path.includes('dashboard')

    // profile app
    if (
      isRedirectToNewLayout ||
      (to.path.includes('dashboard') && isToNewLayout('home'))
    ) {
      await generateCookie()

      const host = env.VUE_APP_NEW_PROFILE_HOST
      window.location = `${host}/home?l=${localStorage.languageabrev}`

      return
    }

    // new engagement app - survey
    if (
      to.path.includes('survey') &&
      !to.path.includes('/custom-surveys') &&
      !to.path.includes('/custom-poll') &&
      isToNewLayout('survey')
    ) {
      await generateCookie()

      const host = env.VUE_APP_NEW_ENGAGEMENT_HOST
      window.location = `${host}/survey?l=${localStorage.languageabrev}`

      return
    }
  }

  if (canAccessNewLayout && to.meta.redirect) {
    const isFromEngagementSummary =
      to.query && to.query.origin === 'engagement-summary'

    const layoutRule = Array.isArray(to.meta.redirect.layoutRule)
      ? to.meta.redirect.layoutRule
      : [to.meta.redirect.layoutRule]

    const shouldRedirectToNewLayout = layoutRule.some(rule => {
      if (typeof rule === 'boolean') {
        return rule
      }

      if (!isFromEngagementSummary && rule === 'engagement-summary') {
        return false
      }

      return isToNewLayout(rule)
    })

    if (shouldRedirectToNewLayout) {
      await redirectToNew(to)
      return
    }
  }

  next()
}

export const beforeEachRouterEntry = (to, from, next) => {
  try {
    juxt([
      setSentryContextUser,
      logoutValidation,
      redirectValidation,
      routerPermissionValidation,
      doNext,
    ])({ to, from, next })
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err)
  }
}

export default { beforeEachRouterEntry }
